import apiClient from './apiClient';
import { toAbsoluteUrl } from '../utils/apiUrl';
import {
  PendingDocument,
  DocumentToSign,
  Signature,
  SigningRecord,
  SigningStats,
  SigningActivity,
  ApiResponse,
  PaginatedResponse,
  SignatureUploadForm,
  DocumentRejectForm,
  SigningForm,
  DocumentFilter,
  DateRange,
  Notification
} from '../types/signer';

export class SignerApiService {
  // Document Management
  static async getPendingDocuments(
    filter?: DocumentFilter,
    page = 1,
    limit = 10
  ): Promise<PaginatedResponse<PendingDocument>> {
    const params: any = {
      page: page.toString(),
      limit: limit.toString(),
    };

    if (filter) {
      if (filter.searchTerm) params.search = filter.searchTerm;
      if (filter.priority) params.priority = filter.priority;
    }

    // Call the pending-activities endpoint
    console.log('🔵 [signerApi] Calling /signer/pending-activities with params:', params);
    const response: any = await apiClient.get('/signer/pending-activities', { params });
    console.log('🔵 [signerApi] Raw response:', response);
    console.log('🔵 [signerApi] response.data:', response?.data);

    // Map API response to PendingDocument format
    // apiClient.get() returns the axios response.data which contains { data: {...}, success: true }
    const apiData = response?.data || {};
    const activities = apiData.activities || [];
    console.log('🔵 [signerApi] apiData:', apiData);
    console.log('🔵 [signerApi] activities:', activities);
    console.log('🔵 [signerApi] activities.length:', activities.length);

    const documents: PendingDocument[] = activities.map((activity: any) => ({
      id: activity.activity_id,
      title: activity.activity_name_th || activity.activity_name_en || 'ไม่มีชื่อกิจกรรม',
      description: activity.activity_name_en || activity.activity_name_th || '',
      activityType: 'workshop', // Default type
      totalRecipients: activity.total_participants || 0,
      requestedBy: {
        id: activity.requested_by_id || '',
        email: 'unknown@example.com',
        first_name: activity.requested_by || 'Unknown',
        last_name: '',
        role: {
          id: 2,
          name: 'staff',
          display_name: 'เจ้าหน้าที่'
        }
      },
      requestDate: activity.requested_date ? new Date(activity.requested_date) : new Date(),
      dueDate: activity.requested_date
        ? new Date(new Date(activity.requested_date).getTime() + 30 * 24 * 60 * 60 * 1000)
        : new Date(),
      priority: this.mapPriority(activity.highest_priority),
      status: activity.status || 'pending_signature',

      // Multi-signer support
      activitySignerId: activity.activity_signer_id,
      signaturePosition: activity.signature_position,
      positionTitle: activity.position_title,
      isRequired: activity.is_required,
      allSigners: activity.all_signers || []
    }));

    return {
      data: documents,
      page: apiData.page || page,
      limit: apiData.limit || limit,
      total: apiData.total || 0,
      totalPages: Math.ceil((apiData.total || 0) / limit)
    };
  }

  /**
   * Map API priority to frontend priority format
   */
  private static mapPriority(apiPriority: string): 'low' | 'medium' | 'high' {
    switch (apiPriority?.toLowerCase()) {
      case 'urgent':
        return 'high';
      case 'high':
        return 'high';
      case 'normal':
      case 'medium':
        return 'medium';
      case 'low':
      default:
        return 'low';
    }
  }

  static async getDocumentDetails(documentId: string): Promise<DocumentToSign> {
    const response = await apiClient.get<ApiResponse<DocumentToSign>>(
      `/signer/documents/${documentId}`
    );
    return response.data;
  }

  /**
   * Get activity details with participants for signing
   */
  static async getActivityDetails(activityId: string): Promise<{
    activity: { id: string; name_th: string; name_en: string; description: string };
    template: { id: string; name: string; preview_url: string };
    participants: Array<{
      id: string;
      student_id: string;
      full_name_th: string;
      full_name_en: string;
      email: string;
      certificate_id: string;
    }>;
    total: number;
    priority: string;
    requested_by: string;
    requested_date: string;
  }> {
    console.log('🔵 [signerApi] Fetching activity details for:', activityId);
    const response: any = await apiClient.get(`/signer/activities/${activityId}`);
    console.log('🔵 [signerApi] Activity details response:', response);
    return response.data;
  }

  static async signDocument(signingData: SigningForm): Promise<void> {
    await apiClient.post('/signer/documents/sign', signingData);
  }

  static async rejectDocument(rejectData: DocumentRejectForm): Promise<void> {
    await apiClient.post('/signer/documents/reject', rejectData);
  }

  // Signature Management
  static async getSignatures(): Promise<Signature[]> {
    const response = await apiClient.get<ApiResponse<Signature[]>>('/signer/signatures');

    // Transform imageUrl to full URL
    const signatures = response.data.map(sig => ({
      ...sig,
      imageUrl: toAbsoluteUrl(sig.imageUrl)
    }));

    return signatures;
  }

  static async uploadSignature(
    formData: FormData,
    onProgress?: (progress: number) => void
  ): Promise<Signature> {
    const response = await apiClient.upload<ApiResponse<Signature>>(
      '/signer/signatures/upload',
      formData,
      onProgress
    );

    // Transform imageUrl to full URL for newly uploaded signature
    const signature = {
      ...response.data,
      imageUrl: toAbsoluteUrl(response.data.imageUrl)
    };

    return signature;
  }

  static async setActiveSignature(signatureId: string): Promise<void> {
    await apiClient.put(`/signer/signatures/${signatureId}/activate`);
  }

  static async deleteSignature(signatureId: string): Promise<void> {
    await apiClient.delete(`/signer/signatures/${signatureId}`);
  }

  // Statistics and Analytics
  // Cache for dashboard data to avoid multiple API calls
  private static dashboardCache: {
    data: any;
    timestamp: number;
  } | null = null;
  private static readonly CACHE_TTL = 30000; // 30 seconds

  private static async fetchDashboard(): Promise<any> {
    const now = Date.now();
    if (this.dashboardCache && (now - this.dashboardCache.timestamp) < this.CACHE_TTL) {
      return this.dashboardCache.data;
    }

    console.log('🔵 [signerApi] Fetching dashboard data');
    const response: any = await apiClient.get('/signer/dashboard');
    console.log('🔵 [signerApi] Dashboard response:', response);

    this.dashboardCache = {
      data: response.data || response,
      timestamp: now
    };

    return this.dashboardCache.data;
  }

  static async getSigningStats(): Promise<SigningStats> {
    try {
      const dashboardData = await this.fetchDashboard();
      const stats = dashboardData?.stats || {};

      // Map backend stats to frontend SigningStats format
      return {
        pendingCount: stats.pending_requests || stats.pendingCount || 0,
        completedThisMonth: stats.signed_this_month || stats.completedThisMonth || 0,
        totalSigned: stats.total_signed || stats.totalSigned || 0,
        averageProcessingTime: stats.avg_processing_time || stats.averageProcessingTime || 0,
        urgentPending: stats.urgent_pending || stats.urgentPending || 0,
        rejectedCount: stats.rejected_requests || stats.rejectedCount || 0,
        activeSignatures: stats.active_signatures || stats.activeSignatures || 0
      };
    } catch (error) {
      console.error('🔴 [signerApi] Error fetching signing stats:', error);
      // Return default stats on error
      return {
        pendingCount: 0,
        completedThisMonth: 0,
        totalSigned: 0,
        averageProcessingTime: 0,
        urgentPending: 0,
        rejectedCount: 0,
        activeSignatures: 0
      };
    }
  }

  static async getDashboardStats(): Promise<{
    stats: SigningStats;
    pendingDocuments: PendingDocument[];
    recentActivities: SigningActivity[];
  }> {
    const dashboardData = await this.fetchDashboard();
    return {
      stats: await this.getSigningStats(),
      pendingDocuments: dashboardData?.pendingDocuments || [],
      recentActivities: dashboardData?.recentActivities || []
    };
  }

  static async getSigningHistory(
    dateRange?: DateRange,
    page = 1,
    limit = 20
  ): Promise<PaginatedResponse<SigningRecord>> {
    const params: any = {
      page: page.toString(),
      limit: limit.toString(),
    };

    if (dateRange) {
      params.startDate = dateRange.startDate.toISOString();
      params.endDate = dateRange.endDate.toISOString();
    }

    return await apiClient.get<PaginatedResponse<SigningRecord>>(
      '/signer/history',
      { params }
    );
  }

  /**
   * Get completed/signed activities
   */
  static async getCompletedActivities(
    page = 1,
    limit = 10
  ): Promise<{
    data: Array<{
      activity_id: string;
      activity_name: string;
      total_certificates: number;
      signed_at: string;
      signed_by: string;
    }>;
    total: number;
    page: number;
    limit: number;
  }> {
    try {
      console.log('🔵 [signerApi] Fetching completed activities');
      const response: any = await apiClient.get('/signer/completed-activities', {
        params: { page, limit }
      });
      console.log('🔵 [signerApi] Completed activities response:', response);
      return response.data || { data: [], total: 0, page, limit };
    } catch (error: any) {
      // If endpoint doesn't exist (404), return empty data
      if (error.response?.status === 404) {
        console.log('🟡 [signerApi] Completed activities endpoint not implemented - returning empty data');
        return { data: [], total: 0, page, limit };
      }
      console.error('🔴 [signerApi] Error fetching completed activities:', error);
      return { data: [], total: 0, page, limit };
    }
  }

  static async getRecentActivity(limit = 10): Promise<SigningActivity[]> {
    try {
      // Use dashboard endpoint which includes recent activities
      const dashboardData = await this.fetchDashboard();
      const activities = dashboardData?.recentActivities || [];

      // Map backend format to frontend SigningActivity format
      return activities.slice(0, limit).map((activity: any) => ({
        id: activity.id || activity.activity_id || '',
        type: activity.type || 'signed',
        documentTitle: activity.description || activity.documentTitle || '',
        timestamp: activity.timestamp ? new Date(parseInt(activity.timestamp) * 1000) : new Date(),
        details: activity.description || activity.details || '',
        priority: activity.priority || 'medium'
      }));
    } catch (error) {
      console.error('🔴 [signerApi] Error fetching recent activity:', error);
      return [];
    }
  }

  // Notifications - Backend endpoint not yet implemented
  // Returns empty data to prevent 404 errors
  static async getNotifications(
    page = 1,
    limit = 20
  ): Promise<PaginatedResponse<Notification>> {
    console.log('🟡 [signerApi] Notifications endpoint not implemented - returning empty data');
    // Return empty paginated response since backend doesn't have this endpoint yet
    return {
      data: [],
      total: 0,
      page,
      limit,
      totalPages: 0
    };
  }

  static async markNotificationAsRead(notificationId: string): Promise<void> {
    // Backend endpoint not implemented - no-op
    console.log('🟡 [signerApi] markNotificationAsRead not implemented');
  }

  static async markAllNotificationsAsRead(): Promise<void> {
    // Backend endpoint not implemented - no-op
    console.log('🟡 [signerApi] markAllNotificationsAsRead not implemented');
  }

  // Reports
  static async exportSigningReport(
    dateRange: DateRange,
    format: 'pdf' | 'excel' = 'pdf'
  ): Promise<Blob> {
    const filename = `signing-report-${dateRange.startDate.toISOString().split('T')[0]}-${dateRange.endDate.toISOString().split('T')[0]}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
    
    return await apiClient.download(
      `/signer/reports/export?startDate=${dateRange.startDate.toISOString()}&endDate=${dateRange.endDate.toISOString()}&format=${format}`,
      filename
    );
  }

  // Utility methods
  static async validateSignatureFile(file: File): Promise<boolean> {
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Only PNG, JPG, and SVG files are allowed.');
    }

    if (file.size > maxSize) {
      throw new Error('File size too large. Maximum size is 5MB.');
    }

    return true;
  }

  static async previewCertificate(
    documentId: string,
    signatureId: string,
    position: { x: number; y: number }
  ): Promise<string> {
    const response = await apiClient.post<ApiResponse<{ previewUrl: string }>>(
      '/signer/documents/preview',
      { documentId, signatureId, position }
    );
    return response.data.previewUrl;
  }
}

// Error handling utility
export const handleApiError = (error: any): string => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred. Please try again.';
};